/** @shell standard @header bar */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRoute } from '@react-navigation/native';
import { Clickable } from '../components/common/Clickable';
import { MainLoader } from '../components/load-main/MainLoader';
import { ScreenHeader, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { CmsPagesApiHtmlScroll } from '../components/common/CmsPagesApiHtmlScroll';
import { cmsPageHtmlToDisplayText } from '../shared/utils/htmlToPlainText';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { fetchCmsPages, findCmsPageByTitleOrMetaTitle, resolveCmsPageLocalizedHtml, resolveCmsPageLocalizedTitle } from '../data/domains/config';
import { useTranslation } from '../shared/i18n';
import { useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { androidAppHeaderContentTop, pillButtonLabelCentered } from '../shared/theme/sharedStyles';
import {
  StackScreenShell,
  studioCardShell,
  DASH_STUDIO_RADIUS,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
} from '../shared/theme/studio/stackScreenShell';

function createTermsAndConditionsStyles(c: ThemeColors) {
  return StyleSheet.create({
    content: {
      flex: 1,
    },
    scrollContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      gap: STACK_SECTION_GAP,
    },
    bodyCard: {
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      padding: appTheme.spacing.lg,
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: '700',
      color: c.text,
      marginTop: 16,
      marginBottom: 8,
    },
    sectionTitleFirst: {
      marginTop: 0,
    },
    paragraph: {
      fontSize: 14,
      lineHeight: 22,
      color: c.textSecondary,
      marginBottom: 12,
    },
    bodyScroll: {
      flex: 1,
    },
    bodyScrollContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
    },
    cmsBodyText: {
      fontSize: 14,
      lineHeight: 22,
      color: c.textSecondary,
    },
    lastUpdated: {
      fontSize: 12,
      color: c.textLight,
      marginTop: 24,
      textAlign: 'center',
      fontStyle: 'italic',
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 50,
    },
    footer: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      paddingTop: appTheme.spacing.md,
      backgroundColor: c.surface,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: c.borderLight,
    },
    acceptButton: {
      backgroundColor: c.primary,
      paddingVertical: 16,
      borderRadius: DASH_STUDIO_RADIUS,
      alignItems: 'center',
      justifyContent: 'center',
    },
    acceptButtonText: {
      ...pillButtonLabelCentered,
      fontSize: 16,
      fontWeight: '600',
      color: c.onPrimary,
    },
  });
}

export const TermsAndConditionsScreen: React.FC = () => {
  const on_back_exit = useExitOrGoBack();
  const route = useRoute();
  const { t, locale } = useTranslation();
  const insets = useSafeAreaInsets();
  const onAccept = (route.params as any)?.onAccept;
  const app_language = locale === 'ar' ? 'ar' : 'en';
  const [content, setContent] = useState<string>('');
  const [page_title, setPageTitle] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const content_top = androidAppHeaderContentTop(insets.top);
  const { scrollY, onScroll } = useScreenHeaderScroll();

  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createTermsAndConditionsStyles(colors), [colors]);
  const header_title = page_title || t('settings.termsAndConditions');

  useEffect(() => {
    fetchContent();
  }, [app_language]);

  const fetchContent = async () => {
    try {
      const pages = await fetchCmsPages();
      const tc_page = findCmsPageByTitleOrMetaTitle(pages, 'Terms & Conditions', 'terms');
      if (tc_page) {
        setContent(resolveCmsPageLocalizedHtml(tc_page, app_language));
        setPageTitle(resolveCmsPageLocalizedTitle(tc_page, app_language));
      } else {
        setContent('');
        setPageTitle('');
      }
    } catch (_err) {
      setContent('');
      setPageTitle('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccept = useCallback(() => {
    if (onAccept) {
      onAccept();
    }
    on_back_exit();
  }, [onAccept, on_back_exit]);

  return (
    <StackScreenShell colors={colors}>
      <ScreenHeader title={header_title} onBack={on_back_exit} scrollY={scrollY} />
      {isLoading ? (
        <View style={[styles.loadingContainer, { paddingTop: content_top }]}>
          <MainLoader size={48} color={colors.primary} />
        </View>
      ) : content && cmsPageHtmlToDisplayText(content) ? (
        <CmsPagesApiHtmlScroll
          html={content}
          scrollViewStyle={styles.bodyScroll}
          onScroll={onScroll}
          scrollEventThrottle={16}
          contentContainerStyle={[
            styles.bodyScrollContent,
            {
              paddingTop: content_top,
              paddingBottom: insets.bottom + STACK_SECTION_GAP * 2 + (onAccept ? appTheme.spacing.xxl : 0),
            },
          ]}
          textStyle={styles.cmsBodyText}
        />
      ) : (
        <Animated.ScrollView
          style={styles.content}
          onScroll={onScroll}
          scrollEventThrottle={16}
          contentContainerStyle={[
            styles.scrollContent,
            {
              paddingTop: content_top,
              paddingBottom: insets.bottom + STACK_SECTION_GAP * 2 + (onAccept ? appTheme.spacing.xxl : 0),
            },
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          <View style={styles.bodyCard}>
            <Text style={[styles.sectionTitle, styles.sectionTitleFirst]}>1. Acceptance of Terms</Text>
            <Text style={styles.paragraph}>
              By accessing and using this application, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this
              service.
            </Text>

            <Text style={styles.sectionTitle}>2. Subscription Services</Text>
            <Text style={styles.paragraph}>
              Our meal subscription services are provided on a prepaid basis. Subscriptions are non-refundable once activated. You may freeze your subscription for a limited number of days as
              specified in your plan.
            </Text>

            <Text style={styles.sectionTitle}>3. Delivery</Text>
            <Text style={styles.paragraph}>
              Meals are delivered to the address specified in your account. It is your responsibility to ensure the delivery address is accurate and accessible. We are not responsible for failed
              deliveries due to incorrect address alertrmation.
            </Text>

            <Text style={styles.sectionTitle}>4. Menu Selection</Text>
            <Text style={styles.paragraph}>
              You must select your meals before the daily cutoff time. If no selection is made, default meals will be assigned based on your dietary preferences. Menu items are subject to availability
              and may change without notice.
            </Text>

            <Text style={styles.sectionTitle}>5. Allergies & Dietary Restrictions</Text>
            <Text style={styles.paragraph}>
              It is your responsibility to alertrm us of any meal allergies or dietary restrictions. While we take precautions, we cannot guarantee that our meals are free from allergens. Consume at
              your own risk.
            </Text>

            <Text style={styles.sectionTitle}>6. Cancellation Policy</Text>
            <Text style={styles.paragraph}>Subscriptions may be cancelled at any time. However, no refunds will be issued for remaining days. Freeze days cannot be converted to refunds.</Text>

            <Text style={styles.lastUpdated}>Last updated: December 2024</Text>
          </View>
        </Animated.ScrollView>
      )}

      {onAccept && (
        <View style={[styles.footer, { paddingBottom: insets.bottom + appTheme.spacing.lg }]}>
          <Clickable style={styles.acceptButton} onPress={handleAccept} hitSlop={appTheme.hitSlop?.medium ?? 12} accessibilityLabel={t('terms.acceptButton')} accessibilityRole="button">
            <Text style={styles.acceptButtonText}>{t('terms.acceptButton')}</Text>
          </Clickable>
        </View>
      )}
    </StackScreenShell>
  );
};

export default TermsAndConditionsScreen;
